import { useMemo } from 'react';

import { isUndefined, mapValues, omitBy } from 'lodash-es';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { interpolate } from '#src/app/pages/psf/promoteOrShare/shared/utils';

import type { TranslationName } from './useCopyI18nKeys';
import { useReplacements } from './useReplacements';

type Result = ModelHookResult<{
	// Resolved override strings from the psf_combo_copy_experiment FCM payload:
	// absence means "use the default copy".
	overrides: Partial<Record<TranslationName, string>>;
}>;

export function useCopyOverrides(): Result {
	const {
		data: { psfComboCopyExperiment },
	} = usePsfPromoteOrSharePageData();
	const {
		data: { replacements },
	} = useReplacements();

	const { variation, config, fallback } = psfComboCopyExperiment;
	// fallback resolutions (donation language, exclusion group, errors, bots) keep the default copy
	const variants = fallback ? undefined : config.copy?.variants;
	// fake_control must render byte-identical to control (A/A check), so its own payload is ignored
	const variant = variation === 'fake_control' ? variants?.control : variants?.[variation];

	return {
		data: {
			overrides: useMemo((): Partial<Record<TranslationName, string>> => {
				if (!variant) return {};
				const resolved = mapValues(variant, (element) => element && interpolate(element.value, replacements));
				return omitBy(resolved, isUndefined);
			}, [variant, replacements]),
		},
	};
}
